<template>
  <div class="welcome-story">
    <section class="timezone-selector">
      <p>Time zone for these messages:</p>

      <el-select
        v-model="defaultTimezoneIdentifier"
        @change="saveDataToContentfulWithCurrentlyEditingMessage('')"
        class="timezone"
        size="small"
        placeholder="Time zone"
      >
        <el-option
          v-for="t in timezoneOptions()"
          :key="t.identifier"
          :label="t.displayName"
          :value="t.identifier">
        </el-option>
      </el-select>
    </section>

    <section class="scheduled-welcome-message" v-for="(message, index) in welcomeMessages" :key="message.uuid">
      <div class="guest-name">
        <label data-test-id="field-locale-label">Guest Name {{index+1}}</label>
        <input
          v-model="message.guestName"
          @change="saveDataToContentfulWithCurrentlyEditingMessage(message.uuid)"
          class="cf-form-input"
        >
      </div>
      <div class="pickers">
        <el-date-picker
          v-model="message.date"
          @change="saveDataToContentfulWithCurrentlyEditingMessage(message.uuid)"
          class="date"
          type="date"
          placeholder="Date"
          size="small"
        />

        <el-select
          v-model="message.startTime"
          @change="saveDataToContentfulWithCurrentlyEditingMessage(message.uuid)"
          class="time"
          size="small"
          clearable
          placeholder="Start time"
        >
          <el-option
            v-for="item in timeOptionsBefore(message.endTime)"
            :key="item.value"
            :label="item.label"
            :value="item.value">
          </el-option>
        </el-select>

        <el-select
          v-model="message.endTime"
          @change="saveDataToContentfulWithCurrentlyEditingMessage(message.uuid)"
          class="time"
          size="small"
          clearable
          placeholder="End time"
        >
          <el-option
            v-for="item in timeOptionsAfter(message.startTime)"
            :key="item.value"
            :label="item.label"
            :value="item.value">
          </el-option>
        </el-select>

        <div class="timezone-str">
          {{timezoneAbbrWithDate(message.date)}}
        </div>
      </div>
      <div class="error" :class="{active: message.errorMessage}">
        {{message.errorMessage}}
      </div>

      <div
        class="icon delete"
        @click="onRemoveButtonClicked(message.uuid)"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
          <path
            d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          <path
            d="M0 0h24v24H0z" fill="none"/>
        </svg>
      </div>
    </section>

    <section class="button-row">
      <label
        class="cf-btn-secondary"
        @click="addNewMessage"
      >
        Add new message
      </label>
    </section>

  </div>
</template>

<script>
import moment from 'moment-timezone';
import * as modelUtil from './welcomeStoryModelUtil.js'

const FULL_OPTIONS_FOR_TIME = modelUtil.fullOptionsForTime();

export default {
  name: 'WelcomeStory',
  data() {
    return {
      defaultTimezoneIdentifier: '', // String, such as 'America/New_York' for ET
      defaultTimezone: null,         // Moment Zone object for the identifier
      welcomeMessages: [],           // See modelUtil.createEmptyVueWelcomeMessage for object shape
    };
  },
  watch: {
    defaultTimezoneIdentifier: function() {
      this.defaultTimezone = moment.tz.zone(this.defaultTimezoneIdentifier);
      console.log('set timezone', this.defaultTimezone);
    },
  },
  created() {
    this.loadDataFromContentful();
    if (!this.defaultTimezoneIdentifier) {
      this.defaultTimezoneIdentifier = modelUtil.getDefaultTimezoneIdentifier();
    }
  },
  methods: {
    loadDataFromContentful() {
      const ex = this.$extension;
      const value  = ex.field.getValue();
      console.log('current value', value);

      if (value && value.defaultTimezoneIdentifier) {
        this.defaultTimezoneIdentifier = value.defaultTimezoneIdentifier;
      }

      if (value && value.schedule) {
        for (const cfStory of value.schedule) {
          const vueStory = modelUtil.convertContentfulWelcomeMessageForVue(cfStory);
          this.welcomeMessages.push(vueStory);
        }
      }

      if (this.welcomeMessages.length === 0){
        this.welcomeMessages.push(modelUtil.createEmptyVueWelcomeMessage());
      }
    },
    saveDataToContentfulWithCurrentlyEditingMessage(uuid) {
      console.log('saveDataToContentfulWithCurrentlyEditingMessage', uuid);

      // Check currently editing message is ready to save
      if (uuid && uuid.length > 0) {
        const message = this.welcomeMessageWithId(uuid);
        const error = modelUtil.validateVueWelcomeMessage(message);
        if (error) {
          console.log('User is still editing, not saving for now');
          message.errorMessage = undefined;
          return;
        }
      }

      // Validate
      const hasErrors = this.validateAllWelcomeMessages();
      if (hasErrors) {
        console.log('Having validation errors, not saving for now');
        return;
      }

      // Save to contentful
      this.saveDataToContentful();
    },
    validateAllWelcomeMessages() {
      let hasErrors = false;
      for (const message of this.welcomeMessages) {
        const error = modelUtil.validateVueWelcomeMessage(message);
        if (error) {
          hasErrors = true;
          message.errorMessage = error;
        } else {
          message.errorMessage = undefined;
        }
      }

      const hasTimeErrors = modelUtil.validateAllTimeRanges(this.welcomeMessages);
      
      if( hasTimeErrors ){
        return true;
      }

      return hasErrors;
    },
    saveDataToContentful() {
      console.log('saveDataToContentful');

      const objectToSave = {
        defaultTimezoneIdentifier: this.defaultTimezoneIdentifier,
        schedule: this.welcomeMessages.map(w =>
          modelUtil.convertVueWelcomeMessageForContentful(w, this.defaultTimezoneIdentifier)
        ),
      };

      console.log('====== $extension.field.setValue() =======');
      const ex = this.$extension;
      ex.field.setValue(objectToSave).then((data) => {
        console.log('Successfully set CF field', data);
      }).catch((error) => {
        console.log('error during saving field', error);
      });
    },
    addNewMessage() {
      const hasErrors = this.validateAllWelcomeMessages();
      if (hasErrors) {
        console.log('Having validation errors, skip for now');
        return;
      }
      this.welcomeMessages.push(modelUtil.createEmptyVueWelcomeMessage());
    },
    welcomeMessageWithId(uuid) {
      const message = this.welcomeMessages.filter(m => m.uuid === uuid);
      if (message.length !== 1) {
        console.error('Message with uuid is not length 1', uuid);
      }
      return message[0];
    },
    onRemoveButtonClicked(uuid) {
      let targetIndex;
      let removeImmediately;
      for (let i in this.welcomeMessages) {
        const message = this.welcomeMessages[i];
        if (message.uuid === uuid) {
          targetIndex = i;
          removeImmediately = !message.guestName || message.guestName.length === 0;
          break;
        }
      }

      if (removeImmediately) {
        this.removeWelcomeMessageWithIndex(targetIndex);
      } else {
        this.$extension.dialogs.openConfirm({
          title: 'Deleting welcome message',
          message: `Are you sure you want to delete a welcome message for ${this.welcomeMessages[targetIndex].guestName}?`,
          confirmLabel: 'Yes',
          cancelLabel: 'No',
        }).then((result) => {
          if (result) {
            this.removeWelcomeMessageWithIndex(targetIndex);
          }
        });
      }
    },
    removeWelcomeMessageWithIndex(targetIndex) {
      this.welcomeMessages.splice(targetIndex, 1);
      console.log('Removed target index: ', targetIndex);
      console.log('welcomeMessage: ', this.welcomeMessages);
      this.saveDataToContentfulWithCurrentlyEditingMessage();
    },
    timeOptionsBefore(endTime) {
      const threshold = endTime ? endTime : 24 * 60;
      return FULL_OPTIONS_FOR_TIME.filter(elm => elm.value < threshold);
    },
    timeOptionsAfter(startTime) {
      const threshold = startTime ? startTime : 0;
      return FULL_OPTIONS_FOR_TIME.filter(elm => elm.value > threshold);
    },
    timezoneOptions() {
      return modelUtil.FULL_OPTIONS_FOR_TIMEZONE;
    },
    timezoneAbbrWithDate(d) {
      if (this.defaultTimezone) {
        return modelUtil.getTimezoneAbbrWithTimezoneAndDate(this.defaultTimezone, d);
      } else {
        return '';
      }
    },
  }
};
</script>

<style scoped lang="scss">
  .welcome-story {
    padding-top: 12px;
    padding-bottom: 300px;

    .timezone-selector {
      display: flex;
      justify-content: left;
      align-items: baseline;
      margin-bottom: 16px;

      p, .timezone {
        flex: initial;
      }

      p {
        margin-left: 5px;
        margin-right: 12px;
      }

      .timezone {
        width: 240px;
      }
    }

    section.scheduled-welcome-message {
      position: relative;
      margin-bottom: 12px;
      border: 1px solid rgba(0, 0, 0, .125);
      padding: 16px 16px;
      max-width: 540px;

      .guest-name {
        margin-bottom: 15px;

        input {
          margin-top: 5px;
        }
      }

      .pickers {
        display: flex;
        justify-content: space-between;
        align-items: baseline;

        .date, .time {
          width: 145px;
        }
        .timezone-str {
          width: 22px;
          text-align: right;
          margin-right: 6px;
        }
      }

      .error {
        color: #f05751;
      }

      .error.active {
        padding-left: 1px;
        padding-top: 15px;
      }

      .icon {
        position: absolute;
        top: 10px;
        right: 10px;
        margin: 0 2px;
        width: 24px;
        height: 24px;

        svg {
          fill: #a9b9c0;
        }

        &.delete {
          svg {
            cursor: pointer;
          }

          svg:hover {
            fill: #f05650;
          }
        }
      }
    }

    .button-row {
      max-width: 540px;
      margin-top: 32px;
      text-align: center;
    }
  }
</style>
